import { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Skeleton,
  Grid,
  Box,
  Card,
  IconButton,
  Collapse,
  Chip,
  Tooltip,
  Button,
  TextField,
  MenuItem
} from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, gql } from '@apollo/client';
import Page from '../components/Page';
import { useAccount } from '../hooks/useAccount';
import ContainerMessage from '../components/ContainerMessage';
import HelperTooltip from '../components/HelperTooltip';

const LIST_PRODUCTS = gql`
  query listProducts($domain: ID!, $limit: Int!, $nextToken: String) {
    listProducts(domain: $domain, limit: $limit, nextToken: $nextToken, sortDirection: DESC) {
      items {
        productId
        title
        imgLink
        price
        link
        inStock
        recommendable
        actionPrice {
          price
          fromDate
          toDate
        }
        includingVat
        vatRate
        categoryText
        description
      }
      nextToken
    }
  }
`;

const GET_PRODUCTS_STATS = gql`
  query getProductsStats($eshopId: ID!) {
    getEshop(id: $eshopId) {
      feedProductCount
      lastFeedUpdateAt
      canAssignJobs
      platform
      features
      languageIds
    }
  }
`;

const CREATE_CUSTOMER_JOB = gql`
  mutation CreateCustomerJob($input: CreateCustomerJobInput!) {
    createCustomerJob(input: $input) {
      createdAt
      createdBy
      domain
      result
      jobStatus
      type
      typeCreatedAt
      updatedAt
      validTo
    }
  }
`;

const LIST_CUSTOMER_JOBS = gql`
  query listCustomerJobs($domain: ID!, $typeCreatedAt: String!) {
    listCustomerJobs(
      domain: $domain
      typeCreatedAt: { ge: $typeCreatedAt }
      limit: 5
      sortDirection: DESC
    ) {
      items {
        createdAt
        createdBy
        domain
        error
        jobStatus
        result
        type
        typeCreatedAt
        updatedAt
      }
    }
  }
`;

// returns 2025-08-23T16:22:08.747Z
const getCurrentProductTimestamp = (offset = 0) => {
  const now = new Date();
  now.setHours(now.getHours() + offset);
  return now.toISOString();
};

const limit = 25;

// Languages whose products are stored separately, as productId "<code>#<lang>". Mirrors
// Eshop.IsMultiLangRecommender in be-stack/pkg/types: UpGates always, Shoptet only with the
// shoptetLanguage feature and more than one language. Anything else stores bare codes, so a
// language filter would match nothing. A single language needs no selector.
const productLanguages = (eshop) => {
  const langs = eshop?.languageIds?.filter(Boolean) || [];
  const multiLang =
    eshop?.platform === 'upgates' ||
    (eshop?.platform === 'shoptet' &&
      (eshop?.features || []).includes('shoptetLanguage') &&
      langs.length > 1);
  return multiLang && langs.length > 1 ? langs : [];
};

// The language is filtered here, not in the query: productId is the table's sort key, and
// DynamoDB refuses key attributes in a filter expression, while a key condition only offers a
// prefix (beginsWith) and the language is a suffix. So each page fetches limit × languages
// rows — one code's variants sort next to each other (code#cs, code#de, …), which gives about
// `limit` products per language — and the list shows the selected language's rows. The page
// size depends only on the eshop, so the cached list (keyed by domain) stays consistent when
// the language changes, and switching needs no request.
const pageLimit = (languages) => Math.min(limit * Math.max(languages.length, 1), 1000);

const inLanguage = (productId, lang) => !lang || productId?.endsWith(`#${lang}`);

export default function EcommerceShop() {
  const { t } = useTranslation();
  const {
    eshop: { domain, id }
  } = useAccount();
  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);
  const [isSyncingProducts, setIsSyncingProducts] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  // '' = all languages; null = not chosen yet (defaults to the eshop's first language)
  const [selectedLang, setSelectedLang] = useState(null);

  const handleToggleCard = (productId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };
  const {
    data: statsData,
    loading: statsLoading,
    refetch: refetchStats
  } = useQuery(GET_PRODUCTS_STATS, {
    variables: {
      eshopId: id
    }
  });

  const languages = useMemo(() => productLanguages(statsData?.getEshop), [statsData]);
  const lang = selectedLang ?? languages[0] ?? '';

  // Another eshop has other languages: drop a choice made for the previous one.
  useEffect(() => {
    setSelectedLang(null);
  }, [domain]);

  // Wait for the eshop's languages: they set the page size, and the cached list is kept per
  // domain, so a first page fetched at the wrong size would stay in it.
  const { data, loading, error, fetchMore } = useQuery(LIST_PRODUCTS, {
    variables: {
      domain,
      limit: pageLimit(languages),
      nextToken: null
    },
    skip: statsLoading
  });

  const typeCreatedAt = useMemo(() => `productSync#${getCurrentProductTimestamp(-2)}`, []);

  const {
    data: customerJobsData,
    loading: customerJobsLoading,
    refetch: refetchCustomerJobs
  } = useQuery(LIST_CUSTOMER_JOBS, {
    variables: {
      domain,
      typeCreatedAt
    },
    skip: !statsData?.getEshop?.canAssignJobs
  });

  useEffect(() => {
    refetchStats();
    refetchCustomerJobs();
  }, [domain, refetchStats, refetchCustomerJobs]);

  const loadMore = async () => {
    setFetchMoreLoading(true);
    await fetchMore({
      variables: {
        limit: pageLimit(languages),
        nextToken
      }
    });
    setFetchMoreLoading(false);
  };

  // GraphQL mutations
  const [assignProductSync] = useMutation(CREATE_CUSTOMER_JOB, {
    onCompleted: (data) => {
      // Optimistically update local state after successful creation
      const newCustomerJob = data.createCustomerJob;
      console.log('newCustomerJob', newCustomerJob);
    },
    onError: (error) => {
      console.error('Error adding to blacklist:', error);
    }
  });

  useEffect(() => {
    if (customerJobsData?.listCustomerJobs?.items?.length > 0) {
      // find the last customer job with type productSync by updatedAt
      const lastProductSyncJob = [...customerJobsData.listCustomerJobs.items].sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      )[0];
      // check if lastProductSyncJob.jobStatus is PENDING / IN_PROGRESS
      if (
        lastProductSyncJob.jobStatus === 'PENDING' ||
        lastProductSyncJob.jobStatus === 'IN_PROGRESS'
      ) {
        console.log('setting isSyncingProducts to true because of status');
        setIsSyncingProducts(true);
      } else {
        setIsSyncingProducts(false);
      }
    }
  }, [customerJobsData]);

  if (error) return <h1>ERROR LOADING DATA</h1>;
  const products =
    data?.listProducts?.items
      ?.filter((item) => inLanguage(item.productId, lang))
      .map((item) => ({
        id: item.productId,
        productId: item.productId,
        title: item.title,
        imgLink: item.imgLink,
        price: item.price,
        link: item.link,
        inStock: item.inStock,
        recommendable: item.recommendable,
        actionPrice: item.actionPrice,
        includingVat: item.includingVat,
        vatRate: item.vatRate,
        categoryText: item.categoryText,
        description: item.description
      })) || [];
  const nextToken = data?.listProducts?.nextToken;
  // Empty means the eshop has no products loaded — not that the loaded pages hold none in the
  // selected language, where "show more" must stay reachable.
  const noProductsLoaded = (data?.listProducts?.items?.length || 0) === 0;
  return (
    <Page title={t('pages.products')}>
      <Container>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 5
          }}
        >
          <Typography variant="h4">
            {t('general.products')}
            <Box
              component="span"
              sx={{
                marginLeft: 1
              }}
            >
              {statsData?.getEshop?.feedProductCount > 0 &&
                `(${statsData?.getEshop?.feedProductCount})`}
            </Box>
          </Typography>
          <Typography variant="caption">
            {statsLoading ? (
              <Skeleton width={100} />
            ) : (
              statsData?.getEshop?.lastFeedUpdateAt &&
              (() => {
                const val = new Date(statsData?.getEshop?.lastFeedUpdateAt);
                return t('products.last_update_at', {
                  val,
                  formatParams: {
                    val: {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    }
                  }
                });
              })()
            )}
            <HelperTooltip title={t('products.last_update_tooltip')} />
          </Typography>

          {languages.length > 0 && (
            <TextField
              select
              size="small"
              label={t('products.language')}
              value={lang}
              onChange={(e) => setSelectedLang(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">{t('products.all_languages')}</MenuItem>
              {languages.map((l) => (
                <MenuItem key={l} value={l}>
                  {l}
                </MenuItem>
              ))}
            </TextField>
          )}

          {!statsLoading && statsData?.getEshop?.canAssignJobs && !customerJobsLoading && (
            <Button
              variant="contained"
              color="primary"
              disabled={isSyncingProducts}
              onClick={async () => {
                const createdAt = new Date().toISOString();
                // UTC + 30 days
                const validTo = new Date().getTime() + 1000 * 60 * 60 * 24 * 30;
                await assignProductSync({
                  variables: {
                    input: {
                      domain,
                      type: 'productSync',
                      typeCreatedAt: `productSync#${createdAt}`,
                      jobStatus: 'PENDING',
                      createdAt,
                      validTo
                    }
                  }
                });
                setIsSyncingProducts(true);
              }}
            >
              {isSyncingProducts
                ? t('products.sync_products_in_progress')
                : t('products.sync_products')}
            </Button>
          )}
        </Box>
        {loading && (
          <Grid container spacing={3}>
            {[...Array(limit)].map((_, i) => (
              <Grid key={i} item xs={12} sm={6} md={3}>
                <Skeleton variant="rectangular" height={300} />
              </Grid>
            ))}
          </Grid>
        )}
        {noProductsLoaded ? (
          <ContainerMessage
            title={t('products.no_products_title')}
            subtitle={t('products.no_products_subtitle')}
          />
        ) : (
          <>
            {products.map((product) => (
              <Card key={product.id} sx={{ width: '100%', mb: 3, position: 'relative' }}>
                <IconButton
                  onClick={() => handleToggleCard(product.id)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 1,
                    width: 40,
                    height: 40,
                    transform: expandedCards[product.id] ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}
                >
                  <Icon icon="eva:arrow-ios-forward-fill" width={32} height={32} />
                </IconButton>

                <Box sx={{ display: 'flex', p: 1.5 }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100'
                    }}
                  >
                    <img
                      src={product.imgLink}
                      alt={product.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        aspectRatio: '1/1'
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      p: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}
                  >
                    <Box>
                      <Typography variant="h6" component="div" gutterBottom>
                        <a
                          href={product.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: 'inherit' }}
                          onMouseOver={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                          onMouseOut={(e) => (e.currentTarget.style.textDecoration = 'none')}
                          onFocus={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                          onBlur={(e) => (e.currentTarget.style.textDecoration = 'none')}
                        >
                          {product.title}
                        </a>

                        <Tooltip
                          title={t(
                            product.recommendable
                              ? 'products.recommendable'
                              : 'products.non_recommendable'
                          )}
                          placement="top"
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              bgcolor: product.recommendable ? 'success.main' : 'grey.500',
                              color: 'white',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              ml: 1,
                              verticalAlign: 'middle',
                              cursor: 'pointer'
                            }}
                          >
                            r
                          </Box>
                        </Tooltip>
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                        {product.actionPrice && product.actionPrice.price > 0 ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" color="error">
                              {product.actionPrice.price}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ textDecoration: 'line-through' }}
                            >
                              {product.price}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="h6" color="secondary">
                            {product.price}
                          </Typography>
                        )}
                        {product.inStock && (
                          <Chip
                            label={t('products.in_stock')}
                            color={product.inStock ? 'success' : 'error'}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              height: '20px',
                              '& .MuiChip-label': {
                                fontSize: '0.7rem',
                                padding: '0 6px'
                              }
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Collapse in={expandedCards[product.id]}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderTop: '1px solid',
                      borderColor: 'grey.200'
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      {t('products.raw_product_data')}
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: 'grey.100',
                        p: 2,
                        borderRadius: 1,
                        overflow: 'auto',
                        fontSize: '0.875rem',
                        fontFamily: 'monospace'
                      }}
                    >
                      {JSON.stringify(product, null, 2)}
                    </Box>
                  </Box>
                </Collapse>
              </Card>
            ))}
            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              sx={{ my: 2 }}
              onClick={loadMore}
              disabled={!nextToken}
              loading={fetchMoreLoading || loading}
            >
              {t('general.show_more')}
            </LoadingButton>
          </>
        )}
      </Container>
    </Page>
  );
}
